import React, { useState, useEffect, useContext, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { FaEye, FaEdit, FaTrashAlt } from 'react-icons/fa';
import { confirmAlert } from 'react-confirm-alert';

import { UserContext } from '../../UserContext';
import { useHttpClient } from '../hooks/useHttpClient';
import { useNotificacoes } from '../hooks/useNotificacoes';
import Pagination from '../pagination/Pagination';

import styles from './ListaProducaoUsinas.module.css';
import styles_global from '../css/global.module.css';
import styles_content from '../css/main-content.module.css';

function ListaProducaoUsinas() {
    const { user } = useContext(UserContext);
    const navigate = useNavigate();
    const { get, del } = useHttpClient();
    const { exibirErro } = useNotificacoes();

    const idEmpresa = user?.empresa?.id;
    const [usinas, setUsinas] = useState([]);
    const [producao, setProducao] = useState([]);
    const [filtros, setFiltros] = useState({ idUsina: '', mes: '', page: 0, pageSize: 10 });
    const [totalPages, setTotalPages] = useState(0);
    const [loading, setLoading] = useState(false);
    const inicializou = useRef(false);

    useEffect(() => {
        if (!user) navigate('/login');
    }, [user, navigate]);

    // Carrega usinas e produção na montagem
    useEffect(() => {
        if (!idEmpresa) return;
        inicializou.current = true;

        async function carregarTudo() {
            try {
                const u = await get(`/hello-sol/usinas/buscar?idEmpresa=${idEmpresa}`);
                setUsinas(Array.isArray(u) ? u : u.content ?? []);
            } catch {
                exibirErro('Erro ao buscar usinas.');
            }
            fetchProducao(0);
        }

        carregarTudo();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [idEmpresa]);

    const fetchProducao = async (page) => {
        setLoading(true);
        try {
            const params = new URLSearchParams({
                idEmpresa,
                ...(filtros.idUsina && { idUsina: filtros.idUsina }),
                ...(filtros.mes && { mes: filtros.mes }),
                page: page.toString(),
                pageSize: filtros.pageSize.toString()
            }).toString();

            const resp = await get(`/hello-sol/producao-usinas?${params}`);
            setProducao(resp.content || []);
            setTotalPages(resp.totalPages || 0);
            setFiltros(f => ({ ...f, page }));
        } catch (err) {
            exibirErro('Erro ao buscar produção: ' + err.message);
        } finally {
            setLoading(false);
        }
    };

    const handleChange = e => {
        const { name, value } = e.target;
        setFiltros(f => ({ ...f, [name]: value }));
    };

    const handleSubmit = e => {
        e.preventDefault();
        fetchProducao(0);
    };

    const confirmDelete = id => {
        confirmAlert({
            title: 'Confirmação',
            message: 'Deseja realmente excluir esta produção de usina?',
            buttons: [
                {
                    label: 'Sim',
                    onClick: async () => {
                        try {
                            await del(`/hello-sol/producao-usinas/${id}`);
                            fetchProducao(filtros.page);
                        } catch (err) {
                            exibirErro(err.message);
                        }
                    }
                },
                {
                    label: 'Não'
                }
            ],
        });
    };

    const handlePageChange = page => {
        if (page >= 0 && page < totalPages) {
            fetchProducao(page);
        }
    };

    return (
        <div className={styles_global.container}>
            <h2>Consultar Produção de Usinas</h2>

            <form onSubmit={handleSubmit} className={styles.filterForm}>
                <div className={styles_content.formField}>
                    <label htmlFor="idUsina">Usina</label>
                    <select id="idUsina" name="idUsina" value={filtros.idUsina} onChange={handleChange}>
                        <option value="">Todas as usinas</option>
                        {usinas.map(u => (
                            <option key={u.id} value={u.id}>{u.nome}</option>
                        ))}
                    </select>
                </div>

                <div className={styles_content.formField}>
                    <label htmlFor="mes">Mês</label>
                    <select id="mes" name="mes" value={filtros.mes} onChange={handleChange}>
                        <option value="">Todos os meses</option>
                        {Array.from({ length: 12 }, (_, i) => (
                            <option key={i+1} value={i+1}>
                                {new Date(0, i).toLocaleString('pt-BR', { month: 'long' }).replace(/^\w/, c => c.toUpperCase())}
                            </option>
                        ))}
                    </select>
                </div>

                <div className={styles.botoesAbaixo}>
                    <button type="submit" disabled={loading}>
                        {loading ? 'Consultando...' : 'Consultar'}
                    </button>
                    <button type="button" onClick={() => navigate('/home/cadastrar-producao-usina')}>
                        Nova Produção
                    </button>
                </div>
            </form>

            {loading && <p className={styles_global.loading}>Carregando...</p>}

            {!loading && producao.length > 0 && (
                <>
                    <div className={styles.tableContainer}>
                        <table className={styles.table}>
                            <thead>
                            <tr>
                                <th>Usina</th>
                                <th>Mês de Produção</th>
                                <th>Período</th>
                                <th>Ações</th>
                            </tr>
                            </thead>
                            <tbody>
                            {producao.map((item, idx) => (
                                <tr key={item.id} className={idx % 2 === 0 ? 'even-row' : 'odd-row'}>
                                    <td>{item.usina.nome}</td>
                                    <td>
                                        {new Date(0, item.mesProducao - 1)
                                            .toLocaleString('pt-BR', {month: 'long'})
                                            .replace(/^\w/, c => c.toUpperCase())}
                                    </td>
                                    <td>{item.dtInicioProducao} a {item.dtFimProducao}</td>
                                    <td className={styles.actions}>
                                        <Link to={`/home/detalhar-producao-usina/${item.id}`}
                                              className={styles.iconButton}>
                                            <FaEye title="Visualizar" className={styles.icon}/>
                                        </Link>
                                        <Link to={`/home/cadastrar-producao-usina/${item.id}`}
                                              className={styles.iconButton}>
                                            <FaEdit title="Editar" className={styles.icon}/>
                                        </Link>
                                        <Link to="#" onClick={() => confirmDelete(item.id)}
                                              className={styles.iconButton}>
                                            <FaTrashAlt title="Excluir" className={`${styles.icon} ${styles.delete}`}/>
                                        </Link>
                                    </td>
                                </tr>
                            ))}
                            </tbody>
                        </table>
                    </div>

                    <Pagination
                        currentPage={filtros.page}
                        totalPages={totalPages}
                        onPageChange={handlePageChange}
                    />
                </>
            )}

            {!loading && producao.length === 0 && inicializou.current && (
                <p className={styles_global.noData}>Nenhum registro encontrado.</p>
            )}
        </div>
    );
}

export default ListaProducaoUsinas;
